import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  X,
  Search,
  Calendar,
  MapPin,
  Users,
  Loader2,
  Copy,
  AlertCircle,
  CheckCircle2,
  ChevronLeft,
} from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { useApi } from '../../contexts/ApiContext';

export default function CopyEntriesModal({
  isOpen,
  onClose,
  eventId,
  onImportComplete,
}) {
  const api = useApi();
  const [step, setStep] = useState('pick-event');
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [driverSearch, setDriverSearch] = useState('');
  const [selectedDriverIds, setSelectedDriverIds] = useState(() => new Set());

  useEffect(() => {
    if (!isOpen) {
      setStep('pick-event');
      setSelectedEvent(null);
      setImportResult(null);
      setSearchTerm('');
      setImportError(null);
      setDriverSearch('');
      setSelectedDriverIds(new Set());
    }
  }, [isOpen]);

  const { data: events = [], isLoading: loading, error: queryError } = useQuery({
    queryKey: ['copyable-events', eventId],
    queryFn: async () => {
      const result = await api.events.getCopyableEvents(eventId);
      if (!result.success) throw new Error(result.message || 'Failed to load events');
      return result.data || [];
    },
    enabled: isOpen && !!eventId,
  });

  const {
    data: sourceEntries = [],
    isLoading: loadingEntries,
    error: entriesError,
  } = useQuery({
    queryKey: ['source-entries', eventId, selectedEvent?.id],
    queryFn: async () => {
      const result = await api.events.getSourceEntries(eventId, selectedEvent.id);
      if (!result.success) throw new Error(result.message || 'Failed to load entries');
      return result.data || [];
    },
    enabled: isOpen && step === 'pick-drivers' && !!selectedEvent?.id,
  });

  useEffect(() => {
    if (step === 'pick-drivers' && sourceEntries.length > 0 && selectedDriverIds.size === 0) {
      setSelectedDriverIds(new Set(sourceEntries.map(e => e.driver_ID)));
    }
  }, [step, sourceEntries]); // eslint-disable-line react-hooks/exhaustive-deps

  const error = importError || queryError?.message || entriesError?.message || null;

  const filteredEvents = useMemo(() => events.filter(event =>
    event.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    event.track_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    event.promotion_name?.toLowerCase().includes(searchTerm.toLowerCase())
  ), [events, searchTerm]);

  const filteredEntries = useMemo(() => {
    const q = driverSearch.toLowerCase();
    if (!q) return sourceEntries;
    return sourceEntries.filter(e =>
      e.driver_name?.toLowerCase().includes(q) ||
      e.car_number?.toString().includes(driverSearch) ||
      e.team?.toLowerCase().includes(q)
    );
  }, [sourceEntries, driverSearch]);

  const allFilteredSelected = filteredEntries.length > 0 &&
    filteredEntries.every(e => selectedDriverIds.has(e.driver_ID));

  const toggleDriver = (driverId) => {
    setSelectedDriverIds(prev => {
      const next = new Set(prev);
      if (next.has(driverId)) next.delete(driverId);
      else next.add(driverId);
      return next;
    });
  };

  const toggleAllFiltered = () => {
    setSelectedDriverIds(prev => {
      const next = new Set(prev);
      if (allFilteredSelected) {
        filteredEntries.forEach(e => next.delete(e.driver_ID));
      } else {
        filteredEntries.forEach(e => next.add(e.driver_ID));
      }
      return next;
    });
  };

  const handleImport = async () => {
    if (!selectedEvent) return;
    if (selectedDriverIds.size === 0) {
      setImportError('Select at least one driver to import');
      return;
    }

    setImporting(true);
    setImportError(null);
    try {
      const result = await api.events.importEntriesFrom(
        eventId,
        selectedEvent.id,
        Array.from(selectedDriverIds)
      );
      if (result.success) {
        setImportResult({
          success: true,
          message: result.data?.message || `Imported ${result.data?.entries?.length || 0} entries`,
          entries: result.data?.entries || [],
          skipped: result.data?.skipped || 0,
        });

        if (result.data?.entries?.length > 0 && onImportComplete) {
          onImportComplete(result.data.entries);
        }
      } else {
        setImportError(result.message || 'Failed to import entries');
      }
    } catch (err) {
      console.error('Error importing entries:', err);
      setImportError('Failed to import entries');
    } finally {
      setImporting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (!isOpen) return null;

  const showResult = !!importResult;
  const showDriverPicker = step === 'pick-drivers' && !showResult;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      <div className="relative bg-white rounded-lg shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
        <div className="bg-gradient-to-r from-wrg-blue-600 to-wrg-blue-500 px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded bg-white/10 flex items-center justify-center">
              <Copy className="w-4 h-4 text-white" />
            </div>
            <div>
              <h2 className="text-md font-semibold text-white">Copy Entry List</h2>
              <p className="text-xs text-blue-100">
                {showResult
                  ? 'Done'
                  : showDriverPicker
                    ? `Pick drivers from ${selectedEvent?.name || 'source event'}`
                    : 'Select an event to copy entries from'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded hover:bg-white/10 transition-colors"
          >
            <X className="w-4 h-4 text-white" />
          </button>
        </div>

        <div className="p-4 overflow-y-auto flex-1">
          {showResult ? (
            <div className="text-center py-8">
              <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-800 mb-2">
                {importResult.message}
              </h3>
              {importResult.skipped > 0 && (
                <p className="text-sm text-slate-500 mb-4">
                  {importResult.skipped} driver(s) were already entered
                </p>
              )}
              <Button onClick={onClose}>Done</Button>
            </div>
          ) : showDriverPicker ? (
            <>
              <div className="mb-3 flex items-center gap-2">
                <Input
                  placeholder="Search drivers..."
                  value={driverSearch}
                  onChange={(e) => setDriverSearch(e.target.value)}
                  leftIcon={Search}
                  size="sm"
                  className="flex-1"
                />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={toggleAllFiltered}
                  disabled={filteredEntries.length === 0}
                >
                  {allFilteredSelected ? 'Clear visible' : 'Select visible'}
                </Button>
              </div>

              {error && (
                <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                  <span className="text-sm text-red-700">{error}</span>
                </div>
              )}

              {loadingEntries ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 text-slate-400 animate-spin" />
                </div>
              ) : filteredEntries.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">
                    {driverSearch ? 'No drivers match your search' : 'No drivers found in source event'}
                  </p>
                </div>
              ) : (
                <div className="border border-slate-200 rounded divide-y divide-slate-100 max-h-[380px] overflow-y-auto">
                  {filteredEntries.map(entry => {
                    const checked = selectedDriverIds.has(entry.driver_ID);
                    return (
                      <label
                        key={entry.driver_ID}
                        className={`flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-slate-50 ${checked ? 'bg-wrg-blue-50' : ''}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleDriver(entry.driver_ID)}
                          className="w-4 h-4 rounded border-slate-300 text-wrg-blue-600 focus:ring-wrg-blue-500"
                        />
                        <span className="w-12 text-xs font-mono text-slate-600">
                          {entry.car_number || '-'}
                        </span>
                        <span className="flex-1 text-sm text-slate-800 truncate">
                          {entry.driver_name}
                        </span>
                        {entry.team && (
                          <span className="text-xs text-slate-500 truncate max-w-[140px]">{entry.team}</span>
                        )}
                      </label>
                    );
                  })}
                </div>
              )}

              <div className="mt-2 text-xs text-slate-500">
                {selectedDriverIds.size} of {sourceEntries.length} selected
              </div>
            </>
          ) : (
            <>
              <Input
                placeholder="Search events..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                leftIcon={Search}
                size="sm"
                className="mb-4"
              />

              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                  <span className="text-sm text-red-700">{error}</span>
                </div>
              )}

              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 text-slate-400 animate-spin" />
                </div>
              ) : filteredEvents.length === 0 ? (
                <div className="text-center py-12">
                  <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">
                    {searchTerm ? 'No events match your search' : 'No previous events found'}
                  </p>
                </div>
              ) : (
                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  {filteredEvents.map(event => (
                    <div
                      key={event.id}
                      onClick={() => setSelectedEvent(event)}
                      className={`p-3 rounded-lg border cursor-pointer transition-all ${
                        selectedEvent?.id === event.id
                          ? 'border-wrg-blue-600 bg-wrg-blue-50 ring-2 ring-wrg-blue-600/20'
                          : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h4 className="font-medium text-slate-800 truncate">
                              {event.name}
                            </h4>
                            {event.same_promotion === 1 && (
                              <span className="px-1.5 py-0.5 text-2xs font-medium bg-green-100 text-green-700 rounded">
                                Same Promotion
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {formatDate(event.event_date)}
                            </span>
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {event.track_name}
                              {event.track_city && `, ${event.track_city}`}
                              {event.state_abbr && `, ${event.state_abbr}`}
                            </span>
                          </div>
                          {event.series_names && (
                            <div className="mt-1 text-xs text-slate-400">
                              {event.series_names}
                            </div>
                          )}
                        </div>
                        <div className="flex-shrink-0 ml-4">
                          <div className="flex items-center gap-1 px-2 py-1 bg-slate-100 rounded text-xs font-medium text-slate-600">
                            <Users className="w-3 h-3" />
                            {event.entry_count}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {!showResult && (
          <div className="border-t border-slate-200 px-4 py-3 bg-slate-50 flex items-center justify-between">
            {showDriverPicker ? (
              <Button
                variant="ghost"
                size="sm"
                leftIcon={ChevronLeft}
                onClick={() => {
                  setStep('pick-event');
                  setSelectedDriverIds(new Set());
                  setDriverSearch('');
                  setImportError(null);
                }}
              >
                Back
              </Button>
            ) : (
              <Button variant="ghost" size="sm" onClick={onClose}>
                Cancel
              </Button>
            )}
            {showDriverPicker ? (
              <Button
                size="sm"
                onClick={handleImport}
                disabled={selectedDriverIds.size === 0 || importing}
                leftIcon={importing ? Loader2 : Copy}
                className={importing ? 'animate-pulse' : ''}
              >
                {importing ? 'Importing...' : `Import ${selectedDriverIds.size} ${selectedDriverIds.size === 1 ? 'entry' : 'entries'}`}
              </Button>
            ) : (
              <Button
                size="sm"
                onClick={() => setStep('pick-drivers')}
                disabled={!selectedEvent}
              >
                Next: pick drivers
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
